import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, ConfirmationModal, DropdownMenu, DropdownMenuItem, IconButton, Row, Typography, useOverlayContext } from 'seagull';
import { ChevronDown, ChevronUp, Edit, Kebab, Trash } from 'seagull/icons';
import { BookmarkAdd } from 'seagull/icons/content-edit';
import { getTestIds } from 'seagull/utils/TestIds';
import { DisplayScopeCheckbox } from '~/components/DisplayScopeCheckbox/DisplayScopeCheckbox';
import { useLocaleKeys } from '~/i18n';
import { isCustomPageContentInvalid, supportsDisplayScope } from '~/models/custom-page/model';
import s from './CustomPageCard.module.scss';
import { CustomPageContent } from './CustomPageContent/CustomPageContent';
import { EditTitlePopover } from './EditTitlePopover/EditTitlePopover';
import { SaveAsTemplateModal } from './SaveAsTemplateModal/SaveAsTemplateModal';
export const GetCustomPageCardTID = (parentTestId)=>getTestIds(parentTestId);
export const CustomPageCard = (param)=>{
    let { page, isReadOnly, expanded, onToggleExpanded, submitAttempted, onTitleChange, onRemove, onPageUpdate, onDisplayScopeChange, isDragActive, dragHandle, getSourceId, lockedExisting, lockedDisplayScope, pageChange, testId } = param;
    var _ref, _ref1;
    const { t } = useLocaleKeys();
    const testIds = useMemo(()=>GetCustomPageCardTID(testId), [
        testId
    ]);
    const { showModal } = useOverlayContext();
    const [editAnchorEl, setEditAnchorEl] = useState(null);
    const isEditPopoverOpen = editAnchorEl != null;
    const [templateSaveAttempted, setTemplateSaveAttempted] = useState(false);
    const [contentResetKey, setContentResetKey] = useState(0);
    const previousPageChangeKindRef = useRef((_ref = pageChange === null || pageChange === void 0 ? void 0 : pageChange.kind) !== null && _ref !== void 0 ? _ref : 'none');
    const currentPageChangeKind = (_ref1 = pageChange === null || pageChange === void 0 ? void 0 : pageChange.kind) !== null && _ref1 !== void 0 ? _ref1 : 'none';
    useEffect(()=>{
        if (previousPageChangeKindRef.current === 'updated' && currentPageChangeKind === 'none') {
            setContentResetKey((k)=>k + 1);
        }
        previousPageChangeKindRef.current = currentPageChangeKind;
    }, [
        currentPageChangeKind
    ]);
    const handleEditClick = useCallback((e)=>{
        e.stopPropagation();
        setEditAnchorEl(e.currentTarget);
    }, []);
    const handleEditPopoverClose = useCallback(()=>{
        setEditAnchorEl(null);
    }, []);
    const handleRemoveClick = useCallback((e)=>{
        e.stopPropagation();
        showModal({
            biName: 'custom_page_card_custom_pages_remove_page_confirm',
            closeOnClickOutside: true,
            closeOnEscapeKey: true,
            content: (param)=>{
                let { dismissModal } = param;
                return /*#__PURE__*/ _jsx(ConfirmationModal, {
                    bi: {
                        primaryButton: {
                            name: 'custom_page_card_confirm'
                        },
                        secondaryButton: {
                            name: 'custom_page_card_cancel'
                        }
                    },
                    skin: "danger",
                    title: t.customPages.removePageConfirmTitle(),
                    content: t.customPages.removePageConfirmMessage(),
                    primaryButtonLabel: t.customPages.remove(),
                    secondaryButtonLabel: t.common.buttons.cancel(),
                    onPrimaryButtonClick: ()=>{
                        onRemove === null || onRemove === void 0 ? void 0 : onRemove(page.id);
                        dismissModal();
                    },
                    onSecondaryButtonClick: ()=>dismissModal(),
                    onCloseButtonClick: ()=>dismissModal()
                });
            }
        });
    }, [
        onRemove,
        page.id,
        showModal,
        t
    ]);
    const handleSaveAsTemplateClick = useCallback((e)=>{
        e.stopPropagation();
        if (isCustomPageContentInvalid(page)) {
            setTemplateSaveAttempted(true);
            return;
        }
        showModal({
            biName: 'save_as_custom_page_template_modal',
            content: (param)=>{
                let { dismissModal } = param;
                return /*#__PURE__*/ _jsx(SaveAsTemplateModal, {
                    page: page,
                    sourceId: getSourceId(),
                    dismissModal: dismissModal
                });
            }
        });
    }, [
        page,
        getSourceId,
        showModal
    ]);
    const handleDisplayScopeToggle = useCallback(()=>{
        const newScope = page.displayScope === 'proposalAndAgreement' ? 'proposalOnly' : 'proposalAndAgreement';
        onDisplayScopeChange === null || onDisplayScopeChange === void 0 ? void 0 : onDisplayScopeChange(newScope);
    }, [
        onDisplayScopeChange,
        page.displayScope
    ]);
    const effectiveSubmitAttempted = !!submitAttempted || templateSaveAttempted;
    const showError = !isReadOnly && effectiveSubmitAttempted && isCustomPageContentInvalid(page);
    const cardClassName = "".concat(s.card).concat(showError ? " ".concat(s.cardError) : '').concat(!expanded ? " ".concat(s.collapsed) : '');
    const titleEditable = !isReadOnly && !isDragActive && !lockedExisting;
    const isDisplayScopeChecked = page.displayScope === 'proposalAndAgreement';
    const showDisplayScope = supportsDisplayScope(page) && (!isReadOnly || isDisplayScopeChecked);
    const isDisplayScopeDisabled = !!isReadOnly || !!lockedExisting || !!lockedDisplayScope;
    return /*#__PURE__*/ _jsxs(Box, {
        testId: testIds.CustomPageCardTestIds.Root(),
        className: cardClassName,
        children: [
            /*#__PURE__*/ _jsxs(Row, {
                testId: testIds.CustomPageCardTestIds.Header(),
                className: s.header,
                alignItems: "center",
                onClick: onToggleExpanded,
                children: [
                    !isReadOnly && dragHandle != null && /*#__PURE__*/ _jsx(Box, {
                        testId: testIds.CustomPageCardTestIds.DragHandle(),
                        className: s.dragHandleSlot,
                        children: dragHandle
                    }),
                    /*#__PURE__*/ _jsxs(Row, {
                        testId: testIds.CustomPageCardTestIds.TitleArea(),
                        className: "".concat(s.titleArea).concat(titleEditable ? " ".concat(s.editable) : ''),
                        alignItems: "center",
                        onClick: titleEditable ? handleEditClick : undefined,
                        children: [
                            /*#__PURE__*/ _jsx(Typography, {
                                testId: testIds.CustomPageCardTestIds.Title(),
                                variant: "h5",
                                weight: "normal",
                                noWrap: true,
                                children: page.pageTitle
                            }),
                            titleEditable && /*#__PURE__*/ _jsx(Row, {
                                className: s.editIconButton,
                                testId: testIds.CustomPageCardTestIds.PencilButton(),
                                children: /*#__PURE__*/ _jsx(Edit, {
                                    size: 20
                                })
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs(Row, {
                        className: s.headerActions,
                        alignItems: "center",
                        children: [
                            !isReadOnly && !isDragActive && /*#__PURE__*/ _jsx(_Fragment, {
                                children: /*#__PURE__*/ _jsxs(DropdownMenu, {
                                    target: /*#__PURE__*/ _jsx(IconButton, {
                                        bi: {
                                            name: 'open_custom_page_actions_menu'
                                        },
                                        testId: testIds.CustomPageCardTestIds.KebabButton(),
                                        onClick: (e)=>e.stopPropagation(),
                                        children: /*#__PURE__*/ _jsx(Kebab, {})
                                    }),
                                    children: [
                                        /*#__PURE__*/ _jsx(DropdownMenuItem, {
                                            bi: {
                                                name: 'save_as_template_action'
                                            },
                                            testId: testIds.CustomPageCardTestIds.SaveAsTemplateAction(),
                                            onClick: handleSaveAsTemplateClick,
                                            iconStart: /*#__PURE__*/ _jsx(BookmarkAdd, {
                                                size: 20
                                            }),
                                            children: t.customPages.saveAsTemplate()
                                        }),
                                        /*#__PURE__*/ _jsx(DropdownMenuItem, {
                                            bi: {
                                                name: 'remove_action'
                                            },
                                            testId: testIds.CustomPageCardTestIds.RemoveAction(),
                                            onClick: handleRemoveClick,
                                            disabled: lockedExisting,
                                            tooltipProps: lockedExisting ? {
                                                title: t.customPages.amendments.cannotRemoveExisting()
                                            } : undefined,
                                            skin: "error",
                                            iconStart: /*#__PURE__*/ _jsx(Trash, {
                                                size: 20
                                            }),
                                            children: t.customPages.remove()
                                        })
                                    ]
                                })
                            }),
                            !isReadOnly && !isDragActive && /*#__PURE__*/ _jsx(Box, {
                                className: s.headerDivider
                            }),
                            !isDragActive && /*#__PURE__*/ _jsx(IconButton, {
                                bi: {
                                    name: 'toggle_custom_page_expanded'
                                },
                                testId: testIds.CustomPageCardTestIds.ChevronButton(),
                                onClick: (e)=>{
                                    e.stopPropagation();
                                    onToggleExpanded();
                                },
                                children: expanded ? /*#__PURE__*/ _jsx(ChevronUp, {}) : /*#__PURE__*/ _jsx(ChevronDown, {})
                            })
                        ]
                    })
                ]
            }),
            expanded && !isDragActive && /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsx(Box, {
                        testId: testIds.CustomPageCardTestIds.ContentArea(),
                        className: s.contentArea,
                        children: /*#__PURE__*/ _jsx(CustomPageContent, {
                            page: page,
                            contentResetKey: contentResetKey,
                            isReadOnly: isReadOnly,
                            submitAttempted: effectiveSubmitAttempted,
                            lockedExisting: lockedExisting,
                            getSourceId: getSourceId,
                            onPageUpdate: onPageUpdate,
                            textTestId: testIds.CustomPageCardTestIds.TextPageContent(),
                            pdfTestId: testIds.CustomPageCardTestIds.PdfPageContent(),
                            videoTestId: testIds.CustomPageCardTestIds.VideoPageContent()
                        })
                    }),
                    showDisplayScope && /*#__PURE__*/ _jsx(Box, {
                        className: s.displayScopeRow,
                        children: /*#__PURE__*/ _jsx(DisplayScopeCheckbox, {
                            testId: testIds.CustomPageCardTestIds.DisplayScopeCheckbox(),
                            checked: isDisplayScopeChecked,
                            onChange: handleDisplayScopeToggle,
                            disabled: isDisplayScopeDisabled
                        })
                    }),
                    pageChange != null && /*#__PURE__*/ _jsxs(Box, {
                        testId: testIds.CustomPageCardTestIds.ChangesSection(),
                        className: s.changesSection,
                        children: [
                            /*#__PURE__*/ _jsx(Typography, {
                                variant: "paragraph2",
                                weight: "bold",
                                children: t.customPages.amendments.changesSectionTitle()
                            }),
                            /*#__PURE__*/ _jsxs(Row, {
                                gap: 8,
                                alignItems: "center",
                                children: [
                                    /*#__PURE__*/ _jsx(Typography, {
                                        testId: testIds.CustomPageCardTestIds.ChangesBullet(),
                                        variant: "paragraph2",
                                        children: pageChange.kind === 'updated' ? t.customPages.amendments.sectionUpdatedBullet({
                                            pageTitle: page.pageTitle
                                        }) : t.customPages.amendments.sectionAddedBullet({
                                            pageTitle: page.pageTitle
                                        })
                                    }),
                                    /*#__PURE__*/ _jsx(IconButton, {
                                        bi: {
                                            name: pageChange.kind === 'updated' ? 'custom_page_card_revert_edit' : 'custom_page_card_remove_addition'
                                        },
                                        testId: testIds.CustomPageCardTestIds.ChangesRevertButton(),
                                        onClick: pageChange.onRevert,
                                        tooltip: {
                                            title: pageChange.kind === 'updated' ? t.customPages.amendments.revertEditTooltip() : t.customPages.amendments.removeAdditionTooltip()
                                        },
                                        children: /*#__PURE__*/ _jsx(Trash, {
                                            size: 20
                                        })
                                    })
                                ]
                            })
                        ]
                    })
                ]
            }),
            isEditPopoverOpen && /*#__PURE__*/ _jsx(EditTitlePopover, {
                testId: testIds.CustomPageCardTestIds.EditTitlePopover(),
                anchorEl: editAnchorEl,
                initialValue: page.pageTitle,
                onSave: (newTitle)=>onTitleChange === null || onTitleChange === void 0 ? void 0 : onTitleChange(newTitle),
                onClose: handleEditPopoverClose
            })
        ]
    });
};
